/* eslint-disable @typescript-eslint/no-explicit-any */
import { isArray, isObject } from '@scripts/utils/type-utils';

import {
  camelToKebab,
  camelToSnake,
  kebabToCamel,
  snakeToCamel,
  ConvertCaseFunction,
  ConvertCaseOptions,
} from '@scripts/utils/string-utils';

export type GetObjectValueArgumentType<T> = {
  obj: Record<string, T>;
  key: string;
  defaultValue: T;
  throwError?: boolean;
};

export const getObjectValue = <T>({
  obj,
  key,
  defaultValue,
  throwError = false,
}: GetObjectValueArgumentType<T>): T => {
  if (key in obj) return obj[key];
  if (throwError) throw new Error(`Key "${key}" not found in the object.`);
  return defaultValue;
};

export const removeItemByKey = <T extends string | number | symbol, U>({
  target,
  key,
}: {
  target: Record<T, U>,
  key: T,
}): void => {
  /* eslint-disable-next-line no-param-reassign */
  delete target[key];
};

export const convertObjectKeys = ({
  target,
  convertCase,
  options,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: any,
  convertCase: ConvertCaseFunction,
  options?: ConvertCaseOptions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): any => {
  if (isObject(target)) {
    return Object.keys(target).reduce((result, key) => {
      const newKey = convertCase({ target: String(key), options });
      return Object.assign(
        result,
        {
          [newKey]: convertObjectKeys({
            target: target[key],
            convertCase,
            options,
          }),
        },
      );
    }, {});
  }

  if (isArray(target)) {
    return target.map((elem) => convertObjectKeys({
      target: elem,
      convertCase,
      options,
    }));
  }

  return target;
};

export type ConvertKeyCaseFunction = ({
  target,
  options,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: any,
  options?: ConvertCaseOptions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) => any;

export const camelKeysToKebabKeys: ConvertKeyCaseFunction = ({
  target,
  options = {
    trimEdges: true,
    compressRepeats: true,
  },
}) => convertObjectKeys({
  target,
  convertCase: camelToKebab,
  options,
});

export const camelKeysToSnakeKeys: ConvertKeyCaseFunction = ({
  target,
  options = {
    trimEdges: true,
    compressRepeats: true,
  },
}) => convertObjectKeys({
  target,
  convertCase: camelToSnake,
  options,
});

export const kebabKeysToCamelKeys: ConvertKeyCaseFunction = ({
  target,
  options = {
    trimEdges: true,
    compressRepeats: true,
  },
}) => convertObjectKeys({
  target,
  convertCase: kebabToCamel,
  options,
});

export const snakeKeysToCamelKeys: ConvertKeyCaseFunction = ({
  target,
  options,
}) => convertObjectKeys({
  target,
  convertCase: snakeToCamel,
  options,
});

export const remapKeys = ({
  target,
  map,
}: {
  target: Record<string, any>;
  map: Record<string, string>;
}): Record<string, any> => Object.entries(target).reduce((result, [key, value]) => {
  const newKey = map[key] ? map[key] : key;
  return {
    ...result,
    [newKey]: value,
  };
}, {});

export const parseObject = ({
  target,
  types = {},
}: {
  target: any,
  types?: Record<string, 'number' | 'string' | 'object'>,
}): any => {
  if (!isObject(target)) return target;

  return Object.entries(target).reduce((result, [key, value]) => {
    if (value === null || value === undefined) {
      return Object.assign(result, { [key]: value });
    }
    if (!isArray(value)) {
      if (types[key] === 'number') {
        return Object.assign(result, { [key]: parseInt(value, 10) });
      }
      if (types[key] === 'object') {
        return Object.assign(
          result,
          {
            [key]: JSON.parse(decodeURIComponent(value)),
          },
        );
      }
    } else if (isArray(value)) {
      return Object.assign(
        result,
        {
          [key]: value.map((elem) => {
            if (elem === null || elem === undefined) {
              return elem;
            }
            if (types[key] === 'number') {
              return parseInt(elem, 10);
            } if (types[key] === 'object') {
              return JSON.parse(elem);
            }
            return elem;
          }),
        },
      );
    }

    return Object.assign(result, { [key]: value });
  }, {});
};

export const filterByKey = ({
  target,
  keys,
}: {
  target: Record<string, any>,
  keys: string[],
}): Record<string, any> => Object.entries(target).reduce((result, [key, value]) => {
  if (keys.includes(key)) {
    return Object.assign(result, { [key]: value });
  }
  return result;
}, {});

export const isDeeplyEmptyObject = ({
  target,
}: {
  target: any,
}): boolean => {
  if (target === null || target === undefined || target === '') {
    return true;
  }
  if (isObject(target)) {
    return Object.values(target).every(
      (value) => isDeeplyEmptyObject({ target: value }),
    );
  }
  return false;
};

export const compareObjectsShallow = ({
  source,
  target,
}: {
  source: any;
  target: any;
}): boolean => {
  if (!isObject(source) || !isObject(target)) return false;

  const sourceKeys = Object.keys(source);
  const targetKeys = Object.keys(target);
  if (sourceKeys.length !== targetKeys.length) return false;

  return sourceKeys.every(
    (key) => targetKeys.includes(key) && source[key] === target[key],
  );
};
